import classNames from 'classnames';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import React, { useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { AdvertisementBanner } from '../../components/atoms/advertisementBanner';
import { PrintButton } from '../../components/atoms/printButton';
import { Separator, SeparatorColor } from '../../components/atoms/separator';
import { ASTRenderer } from '../../components/molecules/astRenderer';
import { BreadCrumbs } from '../../components/molecules/breadCrumbs';
import { Seo } from '../../components/molecules/seo';
import { SocialShareIcons } from '../../components/molecules/socialShareIcons';
import { VimeoPlayer } from '../../components/molecules/vimeoPlayer';
import { PostLatestNews } from '../../components/organisms/postLatestNews';
import { CategoriesContext } from '../../contexts';
import { PostQuery } from '../../graphql-generated';
import { useCategoryActivation } from '../../hooks/useCategoryActivation';
import { PageProps } from '../../types/pageProps';
import { decodeHTML } from '../../utils/decodeHTML';
import { adjustToUtc, formatDateFull, isDateStringValid } from '../../utils/formatDate';
import { isNotNull } from '../../utils/isNotNull';
import { createNewsArticleMicroData } from '../../utils/microdata/createNewsArticleMicroData';
import { parseLink } from '../../utils/parseLink';
import { prependHost } from '../../utils/prependHost';
import { prepareDescription } from '../../utils/seo/prepareDescription';
import './post.scss';

export type PostProps = {
  renderLatestNews?: boolean;
  renderSeo?: boolean;
};

const Post = ({
  data,
  path,
  renderLatestNews = true,
  renderSeo = true,
  ...rest
}: PostProps & PageProps<PostQuery>) => {
  const post = data?.post!;
  const title = post.title ?? '';
  const categories = post.categories?.nodes ?? [];
  const tags = post.tags?.nodes || [];
  const absolutePath = prependHost(path);
  const hasVideo = post.acfPostFields?.featuredVideo != null;
  const image = hasVideo
    ? post.acfPostFields?.featuredVideoImg?.childImageSharp?.fluid
    : post.featuredImage?.node?.localFile?.childImageSharp?.fluid;
  const contentImages = post.contentAST?.images?.filter(isNotNull) || [];
  // const isAdvertisement = post.acf?.advertisement?.isAdvertisement;
  const isAdvertisement = post.acfPostAdData?.advertisement?.isadvertisement;
  const description = post.excerpt;
  const hidePageAndLinkForCategory = useContext(
    CategoriesContext
  ).getHidePageAndLinkForCategory(categories[0]?.name ?? '');

  const [ref, inView] = useInView({
    threshold: 0.3,
  });

  const added =  adjustToUtc(post.date) || '';
  const modified = adjustToUtc(post.modified) || '';

  useEffect(() => {
    if (inView) {
      const link = parseLink(post.link);
      if (link !== window.location.pathname) {
        try {
          window.history.replaceState('', post.title ?? '', post.link);
        } catch (e) {
          console.warn("window.history.replaceState couldn't be used", e);
        }
      }
    }
  }, [inView]);

  useCategoryActivation(categories[0]?.name);

  const parsedDescription = prepareDescription(description || '');

  // TODO: Move this to a separate function
  // TODO: Also, clean up them meta below.
  const articleData = createNewsArticleMicroData({
    title: post.title,
    url: absolutePath, // TODO: Change to absolute url instead of relative,
    author: {
      name: post.author?.node?.name ?? '',
    },
    description: parsedDescription,
    imageUrl: prependHost(image?.src ?? ''),
    modifiedDate: added,
    publishedDate: modified,
  });

  return (
    <>
      {renderSeo && (
        <Seo
          title={title}
          description={parsedDescription}
          microData={[articleData]}
          meta={[
            { property: 'og:image', content: prependHost(image?.src) },
            { property: 'og:image:width', content: image?.presentationWidth },
            { property: 'og:image:height', content: image?.presentationHeight },
            { property: 'article:published_time', content: added },
            { property: 'article:modified_time', content: modified },
            post.categories && {
              property: 'article:section',
              content: post.categories?.nodes?.[0]?.name,
            },
          ].filter(Boolean)}
        />
      )}
      {renderLatestNews && <PostLatestNews />}
      <div
        className={classNames(
          'outer',
          isAdvertisement ? 'outer--dark' : 'outer--light'
        )}
      >
        <div className="container">
          {isAdvertisement ? (
            <>
              <AdvertisementBanner
                companyName={post.acfPostAdData?.advertisement?.companyname}
              ></AdvertisementBanner>
            </>
          ) : (
            <BreadCrumbs
              categoryBreadCrumb={{
                name: categories[0]?.name,
                link: hidePageAndLinkForCategory
                  ? undefined
                  : categories[0]?.link,
              }}
              tagsBreadCrumbs={tags.filter(isNotNull)}
            />
          )}

          <article className="post" ref={ref}>
            <div className="post__col-1">
              <h1>{title}</h1>
              {isDateStringValid(added) && <div className="post__date">
                Publicerad {formatDateFull(new Date(added))}
              </div>}
              {hasVideo ? (
                <VimeoPlayer
                  video={post.acfPostFields?.featuredVideo}
                  image={image}
                />
              ) : (
                image && (
                  <Img
                    fluid={image as any}
                    alt={post.featuredImage?.node?.altText ?? ''}
                  />
                )
              )}
              {post.featuredImage?.node?.caption && (
                <div
                  className="post__image-caption"
                  dangerouslySetInnerHTML={{
                    __html: post.featuredImage?.node.caption,
                  }}
                />
              )}

              <div className="post__content">
                <div className="social-share-icons-wrapper">
                  <SocialShareIcons url={absolutePath} />
                </div>
                <div className="post-content-wrapper">
                  {post.contentAST && (
                    <ASTRenderer
                      ast={post.contentAST.ast}
                      images={contentImages}
                    />
                  )}
                  {!isAdvertisement && (
                    <>
                      <div className="post-metadata">
                        <div>
                          {isDateStringValid(added) && <div className="post__published-date">
                            Publicerad&nbsp;{formatDateFull(new Date(added))}
                          </div>}
                          {isDateStringValid(modified) && <div className="post__updated-date">
                            Uppdaterad&nbsp;
                            {formatDateFull(new Date(modified))}
                          </div>}
                        </div>
                        <div className="share-buttons-wrapper">
                          <SocialShareIcons url={absolutePath} />
                        </div>
                      </div>
                      <Separator color={SeparatorColor.Dark} />

                      <section className="author">
                        {post.author?.node?.acfAdditionalSettings
                          ?.displayImage && (
                          <div
                            className="author__avatar"
                            style={{
                              backgroundImage: `url(${post.author?.node?.acfAdditionalSettings?.displayImage?.localFile?.childImageSharp?.fixed?.src})`,
                            }}
                          ></div>
                        )}
                        <div className="inner-wrapper">
                          <div>{post.author?.node?.name}</div>
                          {post.author?.node?.acfAdditionalSettings
                            ?.displayEmail && (
                            <a
                              href={`mailto:${post.author?.node?.acfAdditionalSettings?.displayEmail}`}
                            >
                              {
                                post.author?.node?.acfAdditionalSettings
                                  ?.displayEmail
                              }
                            </a>
                          )}
                        </div>
                      </section>
                      <Separator color={SeparatorColor.Light} />
                      <section className="general">
                        <div className="inner-wrapper">
                          <span
                            className="general__text"
                            dangerouslySetInnerHTML={{
                              __html:
                                data.wp?.options?.acfPostFooterLayout
                                  ?.postFooterText ?? '',
                            }}
                          ></span>
                          <PrintButton className="post__print">
                            Skriv ut
                          </PrintButton>
                        </div>
                        <div
                          className="general__bottom-text"
                          dangerouslySetInnerHTML={{
                            __html:
                              data.wp?.options?.acfPostFooterLayout
                                ?.postFooterBottomText ?? '',
                          }}
                        ></div>
                      </section>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="post__col-2"></div>
          </article>
        </div>
      </div>
    </>
  );
};

export const query = graphql`
  query PostQuery($id: String!) {
    post: wpPost(id: { eq: $id }) {
      ...Post
    }
    wp {
      options {
        acfPostFooterLayout {
          postFooterBottomText
          postFooterText
        }
      }
    }
  }
`;

export default Post;
